import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { UserPlus, Check, X } from 'lucide-react';

interface CohostManagerProps {
  streamId: string;
  isCreator: boolean;
}

export const CohostManager = ({ streamId, isCreator }: CohostManagerProps) => {
  const [cohosts, setCohosts] = useState<any[]>([]);
  const [searchUsername, setSearchUsername] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    loadCohosts();
  }, [streamId]);

  const loadCohosts = async () => {
    const { data, error } = await supabase
      .from('stream_cohosts')
      .select(`
        *,
        profiles:cohost_id (
          username,
          display_name,
          avatar_url
        )
      `)
      .eq('stream_id', streamId);

    if (!error && data) {
      setCohosts(data);
    }
  };

  const inviteCohost = async () => {
    const { data: profile } = await supabase
      .from('profiles')
      .select('id')
      .eq('username', searchUsername)
      .single();

    if (!profile) {
      toast({ title: 'User not found', variant: 'destructive' });
      return;
    }

    const { error } = await supabase
      .from('stream_cohosts')
      .insert({
        stream_id: streamId,
        cohost_id: profile.id,
      });

    if (error) {
      toast({ title: 'Failed to invite cohost', variant: 'destructive' });
    } else {
      toast({ title: 'Cohost invited!' });
      setSearchUsername('');
      loadCohosts();
    }
  };

  const updateCohostStatus = async (cohostId: string, status: string) => {
    const { error } = await supabase
      .from('stream_cohosts')
      .update({ status, accepted_at: status === 'accepted' ? new Date().toISOString() : null })
      .eq('id', cohostId);

    if (!error) {
      toast({ title: `Invitation ${status}` });
      loadCohosts();
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Co-Hosts</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isCreator && (
          <div className="flex gap-2">
            <Input
              placeholder="Enter username to invite"
              value={searchUsername}
              onChange={(e) => setSearchUsername(e.target.value)}
            />
            <Button onClick={inviteCohost} size="icon">
              <UserPlus className="h-4 w-4" />
            </Button>
          </div>
        )}

        <div className="space-y-2">
          {cohosts.map((cohost) => (
            <div key={cohost.id} className="flex items-center justify-between p-2 border rounded">
              <div className="flex items-center gap-2">
                <span>{cohost.profiles?.display_name || cohost.profiles?.username}</span>
                <Badge variant={
                  cohost.status === 'accepted' ? 'default' :
                  cohost.status === 'pending' ? 'secondary' : 'destructive'
                }>
                  {cohost.status}
                </Badge>
              </div>
              {!isCreator && cohost.status === 'pending' && (
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => updateCohostStatus(cohost.id, 'accepted')}>
                    <Check className="h-4 w-4" />
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => updateCohostStatus(cohost.id, 'rejected')}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};
